import {
  GlobeIcon,
  GraduationCapIcon,
  LayersIcon,
  MountainSnowIcon,
  PickaxeIcon,
  SearchIcon,
  SettingsIcon,
  ShieldCheckIcon,
  UsersIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentos de Minería y Recursos Minerales',
    subtitle: 'La base que tus alumnos necesitan para entender todo lo demás',
    classroom:
      'El primer contacto con la minería puede ser el más transformador o el más confuso. Este tomo convierte un tema técnico en algo comprensible y relevante para tus alumnos desde el primer día.',
    items: [
      'Concepto y características de la minería',
      'Recursos y materias primas minerales',
      'Minerales metálicos y no metálicos',
      'Recursos minerales y su aprovechamiento',
      'Principales tipos de minería',
      'Etapas generales de la actividad minera',
      'Exploración, explotación, procesamiento y cierre',
      'Importancia de los minerales en la vida cotidiana',
      'Minería, industria y desarrollo económico',
      'Conceptos fundamentales y terminología minera',
    ],
    result:
      'Tus alumnos entienden la minería como una disciplina práctica y relevante, no como un conjunto de términos imposibles de memorizar.',
    icon: PickaxeIcon,
  },
  {
    number: '2',
    title: 'Geología Aplicada a la Minería',
    subtitle: 'Todo lo que necesitan saber sobre los fundamentos geológicos de la minería',
    classroom:
      'Este es uno de los puntos donde más se traban los alumnos. Este tomo te da el material listo para que comprendan la geología minera sin sobrecarga técnica.',
    items: [
      'Fundamentos de geología aplicada',
      'Minerales y sus principales propiedades',
      'Rocas ígneas, sedimentarias y metamórficas',
      'Ciclo de las rocas',
      'Estructura y dinámica terrestre',
      'Procesos geológicos vinculados con la mineralización',
      'Formación de depósitos minerales',
      'Yacimientos y recursos minerales',
      'Estructuras geológicas relevantes',
      'Relación entre geología, exploración y minería',
      '12 casos prácticos de geología minera',
    ],
    result: 'Tus alumnos aprenden los principios geológicos que explican dónde se encuentran los minerales.',
    icon: LayersIcon,
  },
  {
    number: '3',
    title: 'Exploración y Evaluación de Recursos Minerales',
    subtitle: 'Cómo se descubren y evalúan los depósitos minerales',
    classroom:
      'La exploración minera es fascinante pero compleja. Este tomo te da todo lo que necesitas para que tus alumnos comprendan este proceso.',
    items: [
      'Fundamentos de exploración minera',
      'Etapas generales de una exploración',
      'Reconocimiento inicial del territorio',
      'Información geológica aplicada',
      'Prospección y búsqueda de indicios',
      'Muestreo como concepto y fuente de información',
      'Interpretación de datos',
      'Incertidumbre en la exploración',
      'Recursos y reservas minerales',
      'Evaluación conceptual de recursos',
      'Toma de decisiones basada en evidencias',
      '15 casos prácticos de exploración',
    ],
    result: 'Tus alumnos comprenden cómo se descubren los recursos minerales y qué información se necesita.',
    icon: SearchIcon,
  },
  {
    number: '4',
    title: 'Métodos de Explotación Minera',
    subtitle: 'Cómo se extraen los minerales del subsuelo',
    classroom:
      'Los métodos de explotación son el corazón de la minería. Este tomo los explica de forma clara y accesible.',
    items: [
      'Fundamentos de explotación minera',
      'Minería a cielo abierto',
      'Minería subterránea',
      'Características generales de cada modalidad',
      'Diferencias entre métodos de explotación',
      'Factores que influyen en la selección de un método',
      'Organización general de una explotación',
      'Equipos y maquinaria desde una perspectiva conceptual',
      'Productividad y aprovechamiento de recursos',
      'Seguridad y ambiente asociados a la explotación',
      'Comparación y análisis de distintos escenarios mineros',
      '15 casos prácticos de explotación',
    ],
    result: 'Tus alumnos entienden cómo funcionan los métodos mineros y por qué se selecciona uno u otro.',
    icon: MountainSnowIcon,
  },
  {
    number: '5',
    title: 'Procesamiento y Beneficio de Minerales',
    subtitle: 'Cómo se transforman los minerales extraídos en productos valiosos',
    classroom: 'El procesamiento completa el ciclo minero. Este tomo lo hace comprensible y práctico.',
    items: [
      'Introducción al procesamiento de minerales',
      'Relación entre extracción y procesamiento',
      'Preparación del material',
      'Trituración y molienda como conceptos',
      'Clasificación y separación',
      'Principios generales de concentración',
      'Propiedades utilizadas para separar minerales',
      'Recuperación y aprovechamiento de materiales',
      'Residuos derivados del procesamiento',
      'Uso de agua y recursos',
      'Eficiencia, ambiente y sostenibilidad',
      '15 casos prácticos de procesamiento',
    ],
    result: 'Tus alumnos comprenden cómo el material extraído se transforma en productos de valor.',
    icon: SettingsIcon,
  },
  {
    number: '6',
    title: 'Seguridad, Higiene y Gestión de Riesgos en Minería',
    subtitle: 'Principios de prevención y cultura de seguridad minera',
    classroom: 'La seguridad minera es fundamental. Este tomo la explica de forma responsable y clara.',
    items: [
      'Principios de seguridad minera',
      'Diferencia entre peligro y riesgo',
      'Identificación conceptual de peligros',
      'Evaluación y gestión de riesgos',
      'Prevención de accidentes',
      'Higiene en entornos laborales',
      'Factores humanos y organizacionales',
      'Equipos de protección como concepto preventivo',
      'Señalización y comunicación de riesgos',
      'Cultura de seguridad',
      'Análisis educativo de situaciones de riesgo',
      'Responsabilidad individual y colectiva en la prevención',
      '12 casos prácticos de seguridad',
    ],
    result:
      'Tus alumnos entienden que la seguridad minera es científicamente rigurosa y culturalmente prioritaria.',
    icon: ShieldCheckIcon,
  },
  {
    number: '7',
    title: 'Gestión Ambiental y Sostenibilidad Minera',
    subtitle: 'Cómo la minería se relaciona con el ambiente y la sostenibilidad',
    classroom:
      'El ambiente es un desafío central de la minería. Este tomo lo aborda de forma equilibrada y completa.',
    items: [
      'Minería y medio ambiente',
      'Identificación de aspectos e impactos ambientales',
      'Agua y actividad minera',
      'Suelo y territorio',
      'Calidad del aire',
      'Biodiversidad y ecosistemas',
      'Residuos asociados a la actividad',
      'Prevención y mitigación de impactos',
      'Gestión ambiental',
      'Uso responsable de recursos',
      'Cierre y recuperación ambiental desde una perspectiva conceptual',
      'Minería y desarrollo sostenible',
      '15 casos prácticos de gestión ambiental',
    ],
    result: 'Tus alumnos entienden la minería como una actividad que debe gestionarse responsablemente.',
    icon: GlobeIcon,
  },
  {
    number: '8',
    title: 'Minería, Sociedad y Comunidades',
    subtitle: 'La dimensión social y comunitaria de la minería',
    classroom: 'La minería no es solo técnica; es también sociedad. Este tomo amplía la mirada.',
    items: [
      'Dimensión social de la minería',
      'Minería y territorio',
      'Comunidades vinculadas con proyectos mineros',
      'Impactos sociales positivos y negativos',
      'Percepciones y diferentes perspectivas sobre la actividad',
      'Participación y diálogo',
      'Comunicación entre actores',
      'Conflictos y construcción de acuerdos',
      'Empleo y actividad económica',
      'Desarrollo local',
      'Responsabilidad social',
      'Análisis crítico de situaciones sociales relacionadas con minería',
      '15 casos prácticos de dimensión social',
    ],
    result: 'Tus alumnos comprenden que la minería afecta a las comunidades y que el diálogo es fundamental.',
    icon: UsersIcon,
  },
  {
    number: '9',
    title: 'Enseñanza de la Minería: Estrategias, Actividades y Proyectos Educativos',
    subtitle: 'Tu guía completa como docente',
    classroom: 'Este tomo te da exactamente lo que necesitas para planificar, organizar y evaluar todo el año.',
    items: [
      'Enseñanza de conceptos mineros',
      'Organización y planificación educativa',
      'Estrategias didácticas',
      'Actividades de análisis y aplicación',
      'Aprendizaje basado en problemas',
      'Trabajo con casos educativos',
      'Debate y argumentación',
      'Pensamiento crítico',
      'Integración entre minería, ambiente y sociedad',
      'Desarrollo de proyectos educativos',
      'Evaluación del aprendizaje',
      'Adaptación de contenidos a distintos contextos educativos',
      '10 proyectos educativos listos para adaptar',
    ],
    result: 'Nunca más te preguntas “¿qué doy hoy?”. Todo está planificado y contextualizado.',
    icon: GraduationCapIcon,
  },
];
